import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { CreditCard, ArrowRight, Star, ShieldCheck, Sparkles, Filter, IndianRupee, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: '', label: 'All Cards' },
  { key: 'cashback', label: 'Cashback' },
  { key: 'rewards', label: 'Rewards' },
  { key: 'travel', label: 'Travel' },
  { key: 'fuel', label: 'Fuel' },
  { key: 'lifestyle', label: 'Lifestyle' },
  { key: 'business', label: 'Business' },
  { key: 'premium', label: 'Premium' },
];

const SORTS = [
  { key: 'rating', label: 'Top Rated' },
  { key: 'fee_low', label: 'Lowest Fee' },
  { key: 'income_low', label: 'Lowest Income' },
];

const CreditCardsPage = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState('rating');
  const [compareIds, setCompareIds] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const url = category ? `${API}/credit-cards?category=${category}` : `${API}/credit-cards`;
        const res = await axios.get(url);
        setCards(res.data || []);
      } catch (e) {
        toast.error('Failed to load credit cards');
      } finally {
        setLoading(false);
      }
    })();
  }, [category]);

  const sorted = useMemo(() => {
    const arr = [...cards];
    if (sort === 'rating') arr.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    if (sort === 'fee_low') arr.sort((a, b) => (a.annual_fee || 0) - (b.annual_fee || 0));
    if (sort === 'income_low') arr.sort((a, b) => (a.min_income || 0) - (b.min_income || 0));
    return arr;
  }, [cards, sort]);

  const toggleCompare = (id) => {
    setCompareIds((arr) => {
      if (arr.includes(id)) return arr.filter((x) => x !== id);
      if (arr.length >= 3) {
        toast.error('You can compare up to 3 cards');
        return arr;
      }
      return [...arr, id];
    });
  };

  const compareCards = cards.filter((c) => compareIds.includes(c.id));

  return (
    <div style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>
      <Navbar />

      <section style={{ background: 'linear-gradient(135deg,#FFF7ED 0%,#FFFFFF 100%)', padding: '5rem 1.5rem 2rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#FFE5D0', color: '#9A3412', padding: '0.4rem 0.85rem', borderRadius: 999, fontSize: '0.8rem', fontWeight: 700, marginBottom: '1rem' }}>
            <CreditCard size={14} /> Credit Cards
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.25rem)', color: '#0F172A', margin: 0, lineHeight: 1.1 }}>
            Find Your Perfect <span className="gradient-text-primary">Credit Card</span>
          </h1>
          <p style={{ fontSize: '1.1rem', color: '#475569', marginTop: '0.75rem', maxWidth: 700 }}>
            Compare top credit cards from HDFC, SBI, ICICI, Axis, Kotak & more. Cashback, rewards, travel — apply online in minutes.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1.5rem', color: '#065F46', fontSize: '0.85rem' }}>
            <span style={badge}><ShieldCheck size={14} /> No hidden charges</span>
            <span style={badge}><Sparkles size={14} /> Instant approval</span>
            <span style={badge}><CheckCircle size={14} /> Free credit score</span>
          </div>
        </div>
      </section>

      <section style={{ padding: '2rem 1.5rem 5rem' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.75rem', color: '#0F172A', fontWeight: 700 }}>
            <Filter size={16} style={{ color: '#FF6B35' }} /> Filter by category
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '1.5rem' }}>
            {CATEGORIES.map((c) => (
              <button
                key={c.key || 'all'}
                onClick={() => setCategory(c.key)}
                className={category === c.key ? 'primary-button' : 'outline-button'}
                style={{ fontSize: '0.85rem', padding: '0.45rem 0.95rem' }}
                data-testid={`cc-filter-${c.key || 'all'}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <div style={{ color: '#475569', fontSize: '0.9rem' }}>
              Showing <strong style={{ color: '#0F172A' }}>{sorted.length}</strong> card{sorted.length !== 1 ? 's' : ''}
              {category && ` in ${CATEGORIES.find((c) => c.key === category)?.label}`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: '0.8rem', color: '#475569' }}>Sort by:</span>
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSort(s.key)}
                  className={sort === s.key ? 'primary-button' : 'outline-button'}
                  style={{ fontSize: '0.75rem', padding: '0.3rem 0.7rem' }}
                  data-testid={`cc-sort-${s.key}`}
                >{s.label}</button>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#FF6B35' }}>Loading credit cards...</div>
          ) : sorted.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: '#475569' }}>No cards match the current filters.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.25rem' }}>
              {sorted.map((card) => (
                <CardItem
                  key={card.id}
                  card={card}
                  selected={compareIds.includes(card.id)}
                  onCompareToggle={() => toggleCompare(card.id)}
                  onApply={() => navigate(`/enquiry?service=${encodeURIComponent(card.name)}`)}
                  onView={() => navigate(`/credit-cards/${card.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {compareCards.length > 0 && (
        <ComparisonDrawer
          cards={compareCards}
          onClear={() => setCompareIds([])}
          onRemove={toggleCompare}
          onView={() => navigate(`/credit-cards/compare?ids=${compareIds.join(',')}`)}
        />
      )}
    </div>
  );
};

const CardItem = ({ card, selected, onCompareToggle, onApply, onView }) => {
  const isFree = (card.joining_fee || 0) === 0 && (card.annual_fee || 0) === 0;
  return (
    <div className="service-card" data-testid={`cc-card-${card.id}`} style={{ position: 'relative', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', border: selected ? '2px solid #FF6B35' : undefined }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        {card.bank_logo && <img src={card.bank_logo} alt={card.bank_name} style={{ width: 44, height: 44, borderRadius: 10, background: '#F8FAFC', padding: 4, border: '1px solid #E2E8F0', objectFit: 'contain' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0F172A', lineHeight: 1.25 }}>{card.name}</div>
          <div style={{ fontSize: '0.8rem', color: '#475569' }}>{card.bank_name}</div>
        </div>
        {card.rating > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#FEF3C7', padding: '0.2rem 0.5rem', borderRadius: 6, fontSize: '0.78rem', color: '#92400E', fontWeight: 700 }}>
            <Star size={12} fill="#F59E0B" stroke="#F59E0B" /> {card.rating}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {(card.tags || []).slice(0, 3).map((t) => (
          <span key={t} style={{ background: '#FFF7ED', color: '#9A3412', padding: '0.2rem 0.55rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700 }}>{t}</span>
        ))}
      </div>

      <div style={{ background: '#F8FAFC', padding: '0.75rem', borderRadius: '0.6rem' }}>
        <div style={{ fontSize: '0.75rem', color: '#475569', marginBottom: 2 }}>Reward Rate</div>
        <div style={{ fontSize: '0.88rem', color: '#0F172A', fontWeight: 600 }}>{card.reward_rate}</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <FeeBox label="Joining Fee" value={card.joining_fee} />
        <FeeBox label="Annual Fee" value={card.annual_fee} waiver={card.annual_fee_waiver_at} />
      </div>

      {isFree && (
        <div style={{ background: '#ECFDF5', color: '#065F46', padding: '0.4rem 0.7rem', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4, width: 'fit-content' }}>
          <Sparkles size={12} /> Lifetime Free
        </div>
      )}

      {(card.key_features || []).slice(0, 3).length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {card.key_features.slice(0, 3).map((f, i) => (
            <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: '0.82rem', color: '#0F172A' }}>
              <CheckCircle size={14} style={{ color: '#10B981', flexShrink: 0, marginTop: 2 }} /> <span>{f}</span>
            </li>
          ))}
        </ul>
      )}

      {card.min_income > 0 && (
        <div style={{ fontSize: '0.75rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
          <IndianRupee size={12} /> Min annual income: ₹{(card.min_income).toLocaleString('en-IN')}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        <button onClick={onApply} className="primary-button" style={{ flex: 1, justifyContent: 'center', fontSize: '0.85rem' }} data-testid={`cc-apply-${card.id}`}>
          Apply Now <ArrowRight size={14} />
        </button>
        <button onClick={onView} className="outline-button" style={{ fontSize: '0.78rem', padding: '0.5rem 0.7rem' }} data-testid={`cc-view-${card.id}`}>
          View
        </button>
        <button onClick={onCompareToggle} className="outline-button" style={{ fontSize: '0.78rem', padding: '0.5rem 0.7rem' }} data-testid={`cc-compare-${card.id}`}>
          {selected ? '✓' : '+'}
        </button>
      </div>
    </div>
  );
};

const FeeBox = ({ label, value, waiver }) => (
  <div style={{ background: '#fff', border: '1px solid #E2E8F0', padding: '0.55rem 0.7rem', borderRadius: '0.5rem' }}>
    <div style={{ fontSize: '0.7rem', color: '#64748B' }}>{label}</div>
    <div style={{ fontSize: '0.95rem', color: '#0F172A', fontWeight: 700 }}>
      {value > 0 ? `₹${Number(value).toLocaleString('en-IN')}` : 'NIL'}
    </div>
    {waiver > 0 && <div style={{ fontSize: '0.65rem', color: '#10B981' }}>Waived at ₹{(waiver / 100000).toFixed(1)}L spend</div>}
  </div>
);

const ComparisonDrawer = ({ cards, onClear, onRemove, onView }) => (
  <div data-testid="cc-compare-drawer" style={{
    position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0) + 84px)', left: 16, right: 16, maxWidth: 1100, margin: '0 auto',
    background: '#0F172A', color: '#fff', borderRadius: 16, padding: '0.9rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
    boxShadow: '0 14px 28px rgba(15,23,42,0.25)', zIndex: 80,
  }}>
    <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Compare ({cards.length}/3)</div>
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
      {cards.map((c) => (
        <div key={c.id} style={{ background: 'rgba(255,255,255,0.1)', padding: '0.25rem 0.6rem', borderRadius: 999, fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          {c.name}
          <button onClick={() => onRemove(c.id)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', opacity: 0.7 }}>×</button>
        </div>
      ))}
    </div>
    <button onClick={onClear} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 999, padding: '0.4rem 0.9rem', fontSize: '0.78rem', cursor: 'pointer' }}>Clear</button>
    {cards.length >= 2 && (
      <button onClick={onView} className="primary-button" style={{ fontSize: '0.8rem', padding: '0.4rem 0.9rem' }} data-testid="cc-compare-view">
        View Side-by-Side
      </button>
    )}
  </div>
);

const badge = { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#ECFDF5', padding: '0.3rem 0.7rem', borderRadius: 999, fontWeight: 600 };

export default CreditCardsPage;
